import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles } from "lucide-react";

const RARITY_COLORS = {
  common: "#9ca3af", uncommon: "#34d399", rare: "#38bdf8",
  epic: "#a78bfa", legendary: "#fbbf24", goddess: "#f472b6",
};

/* Global "card awarded" ticker — every logged-in player sees each new award
   flash at the top for ~1 second (person · rarity · card name). */
export default function AwardBroadcast() {
  const { user } = useAuth();
  const sinceRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    (async () => {
      try { const r = await api.get("/award-feed"); if (active) sinceRef.current = r.data.now; } catch (_) { /* silent */ }
    })();
    const poll = async () => {
      if (!sinceRef.current) return;
      try {
        const r = await api.get("/award-feed", { params: { since: sinceRef.current } });
        const items = r.data?.items || [];
        if (items.length) {
          sinceRef.current = items[items.length - 1].created_at;
          setQueue((q) => [...q, ...items]);
        }
      } catch (_) { /* silent */ }
    };
    const iv = setInterval(poll, 4000);
    return () => { active = false; clearInterval(iv); };
  }, [user?.id]);

  // Pull the next award into view when the banner is free.
  useEffect(() => {
    if (current || queue.length === 0) return;
    setCurrent(queue[0]);
    setQueue((q) => q.slice(1));
  }, [queue, current]);

  // Auto-dismiss the current award after ~2.2s. Keyed ONLY on the shown award so
  // incoming queue updates can't cancel the timer (which previously left it stuck).
  useEffect(() => {
    if (!current) return;
    const t = setTimeout(() => setCurrent(null), 2200);
    return () => clearTimeout(t);
  }, [current]);

  const color = current ? (RARITY_COLORS[current.rarity] || "#9ca3af") : "#9ca3af";

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          key={current.id}
          initial={{ y: -36, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -36, opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="fixed top-2 left-1/2 -translate-x-1/2 z-[200] pointer-events-none"
          data-testid="award-broadcast"
        >
          <div className="rounded-full px-4 py-1.5 border bg-black/85 backdrop-blur-md flex items-center gap-2"
               style={{ borderColor: `${color}66`, boxShadow: `0 0 22px ${color}55` }}>
            <Sparkles className="h-3.5 w-3.5 shrink-0" style={{ color }} />
            <span className="text-sm text-white/85 whitespace-nowrap">
              <span className="font-semibold text-white">{current.user_name}</span>
              {" was awarded a "}
              <span className="font-semibold uppercase tracking-wide" style={{ color }}>{current.rarity}</span>
              {" "}
              <span className="text-white">{current.card_title}</span>
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
